import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GetUserStatsAndRankings } from 'src/_shared/api/graphql/custom/dashboard/getUserStatsAndRankingsGraphql.js';

const EMPTY_STATS = {};
const EMPTY_LEADERBOARDS = {
	referralTotal: null,
	referralPoints: null,
	recognitionPoints: null,
	recognitionPointsBySubCompany: null,
};

/**
 * Single source of truth for dashboard stats, rankings and leaderboards.
 *
 * The API decides what a user qualifies for and returns null for anything it
 * skipped, so callers only branch on null rather than re-deriving company
 * settings (publicLeaderboard, pointsSettings.enabled, includeAdminInRanking).
 *
 * The two failure modes stay separate because they are different shapes and
 * mean different things:
 *   userStatsError - ApolloError, or undefined. The request itself failed, so
 *                    there is no data at all.
 *   apiErrors      - array of { message, option }, or null. The request
 *                    succeeded but the API could not resolve some of what was
 *                    asked for; the rest of the payload is still usable.
 */
export const useUserStatsAndRankings = (userId) => {
	const { data, loading, error } = useQuery(GetUserStatsAndRankings, {
		variables: { userId },
		skip: !userId,
		fetchPolicy: 'cache-and-network',
	});

	const result = data?.getUserStatsAndRankings;

	return useMemo(() => {
		const stats = result?.stats ?? EMPTY_STATS;
		const rankings = result?.rankings ?? EMPTY_STATS;

		return {
			userStats: {
				userId: result?.userId,
				companyId: result?.companyId,
				currency: result?.currency,
				bonusTotal: stats.bonusTotal,
				openJobTotal: stats.openJobTotal,
				referralTotal: stats.referralTotal,
				referralPointsTotal: stats.referralPointsTotal,
				recognitionPointsTotal: stats.recognitionPointsTotal,
				recognitionTotal: stats.recognitionTotal,
				ranking: rankings.ranking,
				previousRanking: rankings.previousRanking,
				pointsRanking: rankings.pointsRanking,
			},
			leaderboards: result
				? {
						referralTotal: stats.referralTotalLeaderboard,
						referralPoints: stats.referralPointsLeaderboard,
						recognitionPoints: stats.recognitionPointsLeaderboard,
						recognitionPointsBySubCompany:
							stats.recognitionPointsBySubCompanyLeaderboard,
					}
				: EMPTY_LEADERBOARDS,
			skipped: result?.skipped ?? [],
			userStatsLoading: loading,
			userStatsError: error,
			apiErrors: result?.errors ?? null,
		};
	}, [result, loading, error]);
};
